import React, { useState, useContext } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import axios from 'axios';
import WarningIcon from '@mui/icons-material/Warning';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { OrderType, RerouteMode } from '../../../interfaces/delivery-company/orders';
import CustomDialog from '../custom-dialog/custom-dialog';
import { useSnackBar } from '../../../contexts/snackbar';

interface RerouteConfirmationDialogProps {
  order: OrderType | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedOrder: OrderType) => void;
}

export function RerouteConfirmationDialog({
  order,
  isOpen,
  onClose,
  onSuccess,
}: RerouteConfirmationDialogProps) {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    if (isLoading) return; // Prevent closing while loading
    setIsChecked(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!order || !isChecked) return;

    setIsLoading(true);

    try {
      // Call the reroute API for direct delivery
      await axios.post(
        `${API_ENDPOINT}/api/v1/orders/reroute/`,
        {
          order_ids: [order.order_id],
          reroute_mode: RerouteMode.DIRECT_DELIVERY,
        },
        config
      );

      // Fetch the updated order data
      const response = await axios.get<{ results: OrderType[] }>(
        `${API_ENDPOINT}/api/v1/orders/?order_id__equals=${order.order_id}`,
        config
      );

      if (response.data.results.length > 0) {
        const updatedOrder = response.data.results[0];
        showMessage('Order rerouted and added to batch successfully!');
        setIsChecked(false);
        setIsLoading(false);
        onSuccess(updatedOrder);
      } else {
        showMessage('Failed to fetch updated order data', 'error');
        setIsLoading(false);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        showMessage(e.response?.data?.error || 'Failed to reroute order', 'error');
      } else {
        showMessage('An unexpected error occurred', 'error');
      }
      setIsLoading(false);
    }
  };

  if (!order) return null;

  const destinationBranch = order.next_leg?.destination?.name || 'another branch';

  return (
    <CustomDialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Order Requires Transfer</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Order <strong>{order.order_id}</strong> is scheduled for transfer 
          to <strong>{destinationBranch}</strong> before delivery.
        </Alert>

        <Typography variant="body2" sx={{ mb: 3 }}>
          If you proceed, the order will be rerouted for <strong>direct delivery</strong> from 
          the current branch (<strong>{order.branch?.name || 'current branch'}</strong>) 
          to the customer, skipping the planned transfer.
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              disabled={isLoading}
            />
          }
          label="I confirm that I want to reroute this order for direct delivery"
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleConfirm}
            disabled={!isChecked || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            {isLoading ? 'Rerouting...' : 'Proceed'}
          </Button>
        </Box>
      </DialogContent>
    </CustomDialog>
  );
}
